import {
  useState,
  type CSSProperties,
  type PropsWithChildren,
  type TransitionEvent,
} from "react";

import { cx } from "../cx";
import { useFullscreenTransitionMask } from "../hooks/useFullscreenTransitionMask";
import { FlexHandle } from "../Resizable/FlexHandle";
import type { ConversationPanelState } from "../types";

type PanelSide = "left" | "right";

// The right panel sits outside the shell's canvas collapse, which only follows
// the CircleAI shell state. Fullscreen is therefore a 100% basis that can
// shrink: the canvas has a 0 basis, so it takes no space and the panel fills
// whatever the dock leaves.
const GEOMETRY_CLASS_NAMES = {
  left: {
    fullscreen: "shrink-0 grow",
    docked: "shrink-0 grow-0",
    transition:
      "[transition:width_280ms_cubic-bezier(0.77,0,0.175,1),grow_280ms_cubic-bezier(0.77,0,0.175,1)]",
  },
  right: {
    fullscreen: "shrink grow-0",
    docked: "shrink-0 grow-0",
    transition: "[transition:flex-basis_280ms_cubic-bezier(0.77,0,0.175,1)]",
  },
} as const;

const shouldEndAnimation = (
  event: TransitionEvent<HTMLDivElement>,
  side: PanelSide,
  isClosed: boolean,
) => {
  if (event.currentTarget !== event.target) return false;
  if (side === "right") return event.propertyName === "flex-basis";

  return (
    event.propertyName === "width" ||
    (!isClosed && event.propertyName === "flex-grow")
  );
};

const getPanelStyle = (
  side: PanelSide,
  panelState: ConversationPanelState,
  storedWidth: number,
): CSSProperties => {
  const isClosed = panelState === "closed";

  if (side === "left") return { width: isClosed ? 0 : storedWidth };
  if (isClosed) return { flexBasis: 0 };

  return { flexBasis: panelState === "fullscreen" ? "100%" : storedWidth };
};

const getContentStyle = (
  isFullscreenSettled: boolean,
  storedWidth: number,
): CSSProperties => ({
  width: isFullscreenSettled ? "100%" : storedWidth,
  minWidth: isFullscreenSettled ? 0 : storedWidth,
});

interface ConversationPanelFrameProps {
  "aria-label": string;
  "data-testid": string;
  isResizable?: boolean;
  maxWidth: number;
  minWidth: number;
  onWidthChange?: (width: number) => void;
  panelState: ConversationPanelState;
  resizeAriaLabel: string;
  side: PanelSide;
  storedWidth: number;
}

export const ConversationPanelFrame = ({
  children,
  "aria-label": ariaLabel,
  "data-testid": testId,
  isResizable = true,
  maxWidth,
  minWidth,
  onWidthChange,
  panelState,
  resizeAriaLabel,
  side,
  storedWidth,
}: Readonly<PropsWithChildren<ConversationPanelFrameProps>>) => {
  const isClosed = panelState === "closed";
  const isFullscreen = panelState === "fullscreen";

  const {
    isAnimating,
    isGeometryAnimating,
    isRevealing,
    isTransitionMaskOpaque,
    completeGeometryTransition,
    completeMaskReveal,
  } = useFullscreenTransitionMask(panelState);
  const [isResizing, setIsResizing] = useState(false);
  const isFullscreenSettled = isFullscreen && !isGeometryAnimating;
  const isConversationInteractionBlocked =
    isClosed || isTransitionMaskOpaque || isRevealing;
  const geometryClassNames = GEOMETRY_CLASS_NAMES[side];
  const shouldRenderResizeHandle =
    isResizable && (!isClosed || isAnimating) && !isFullscreen;

  const resizeHandle = shouldRenderResizeHandle && onWidthChange && (
    <FlexHandle
      aria-label={resizeAriaLabel}
      width={storedWidth}
      direction={side === "left" ? 1 : -1}
      minWidth={minWidth}
      maxWidth={maxWidth}
      onResize={onWidthChange}
      onResizeStart={() => setIsResizing(true)}
      onResizeEnd={() => setIsResizing(false)}
      className={cx({
        "pointer-events-none": isClosed && isAnimating,
      })}
    />
  );

  return (
    <>
      {side === "right" && resizeHandle}
      <div
        style={
          isResizable ? getPanelStyle(side, panelState, storedWidth) : undefined
        }
        className={cx(
          "relative isolate min-w-0 overflow-hidden motion-reduce:transition-none",
          {
            [geometryClassNames.fullscreen]: isFullscreen,
            [geometryClassNames.docked]: !isFullscreen,
            [geometryClassNames.transition]: isResizable && !isResizing,
          },
        )}
        onTransitionEnd={event => {
          if (shouldEndAnimation(event, side, isClosed)) {
            completeGeometryTransition();
          }
        }}
      >
        <aside
          data-testid={testId}
          data-state={panelState}
          aria-label={ariaLabel}
          aria-hidden={isConversationInteractionBlocked || undefined}
          inert={isConversationInteractionBlocked || undefined}
          style={
            isResizable
              ? getContentStyle(isFullscreenSettled, storedWidth)
              : undefined
          }
          className={cx(
            "flex size-full min-h-0 shrink-0 flex-col overflow-hidden",
            {
              "border-primary border-l": side === "right",
              "opacity-0": isClosed,
              "opacity-100": !isClosed,
              "duration-[120ms] ease-[cubic-bezier(0.23,1,0.32,1)] transition-opacity":
                !isResizing && isClosed,
              "duration-[180ms] ease-[cubic-bezier(0.23,1,0.32,1)] transition-opacity":
                !isResizing && !isClosed,
            },
          )}
        >
          {!isClosed || isAnimating ? children : null}
        </aside>
        <div
          aria-hidden
          className={cx(
            "pointer-events-none absolute inset-0 z-10 bg-primary dark:bg-secondary",
            {
              "opacity-100 transition-none": isTransitionMaskOpaque,
              "opacity-0": !isTransitionMaskOpaque,
              "duration-[280ms] transition-opacity ease-in-out":
                isRevealing && !isTransitionMaskOpaque,
            },
          )}
          onTransitionEnd={event => {
            if (
              event.currentTarget === event.target &&
              event.propertyName === "opacity"
            ) {
              completeMaskReveal();
            }
          }}
        />
      </div>
      {side === "left" && resizeHandle}
    </>
  );
};

ConversationPanelFrame.displayName =
  "CircleAIAppShell.Layout.ConversationPanel.ConversationPanelFrame";
